/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { Project } from '@/types/models.ts';
import styled from 'styled-components';
import { classNames } from 'primereact/utils';
import { useQuery } from '@tanstack/react-query';
import { useProcessesContext } from 'ogc-processes-components';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { Button } from 'primereact/button';
import { NavLink } from 'react-router';
import { AppRoutes } from '@/constants/routes.ts';
import { useProjectPath } from '@/hooks/useProjectPath';
import { GroupAvatar } from '@/components/GroupAvatar.tsx';
import { QualityBadge } from '@/components/QualityBadge.tsx';
import { DateTime } from 'luxon';
import { useStoreContext } from '@/contexts/StoreContext';
import { Tag } from 'primereact/tag';
import { AntflowApiEndpoint } from '@/services/dataprovider';

interface Props {
  onFilterOnTopics?: (topic: string) => void;
  className?: string;
  project: Project;
  isSpace?: boolean;
}

const Card = styled(NavLink)`
  & {
    cursor: pointer;
    color: var(--text-color);
    text-decoration: none;
    display: flex;
    flex-direction: column;
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 14px;
    overflow: hidden;
    transition:
      transform 200ms ease-out,
      box-shadow 200ms ease-out,
      border-color 200ms ease-out;
    height: 100%;
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
    border-color: color-mix(in srgb, var(--primary-color) 60%, transparent);
  }
  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

const Banner = styled.div<{ $previewImg?: string | null }>`
  position: relative;
  height: 140px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.75rem 0.85rem;
  background-color: color-mix(in srgb, var(--primary-color) 10%, #ffffff 90%);
  ${({ $previewImg }) =>
    $previewImg
      ? `
    background-image: linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%), url('${$previewImg}');
    background-size: cover;
    background-position: center;
    border-bottom: none;
  `
      : `
    background-image: repeating-linear-gradient(
      135deg,
      color-mix(in srgb, var(--primary-color) 18%, transparent) 0 1px,
      transparent 1px 10px
    );
    border-bottom: 1px solid color-mix(in srgb, var(--primary-color) 18%, transparent);
  `}
`;

const StarChip = styled.span<{ $onImage?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: ${({ $onImage }) => ($onImage ? 'rgba(255, 255, 255, 0.95)' : 'var(--surface-card)')};
  backdrop-filter: ${({ $onImage }) => ($onImage ? 'blur(4px)' : 'none')};
  color: ${({ $onImage }) => ($onImage ? 'var(--surface-900)' : 'var(--text-color)')};
  font-size: 0.72rem;
  font-weight: 600;
  border: 1px solid ${({ $onImage }) => ($onImage ? 'rgba(255,255,255,0.3)' : 'var(--surface-border)')};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  ${({ $onImage }) => $onImage && 'transition: all 0.2s ease;'}
  & i {
    font-size: 0.75rem;
    color: ${({ $onImage }) => ($onImage ? '#eab308' : 'var(--text-color-secondary)')};
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  background: var(--surface-50, var(--surface-100));
  border-top: 1px solid var(--surface-border);
`;

const FooterLabel = styled.span`
  display: block;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-color-secondary);
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const cleanReadme = (readme: string): string =>
  readme
    .replace(/^---\n([\s\S]*?)\n---\n/, '')
    .replace(/^\s*#{1,6}\s*(.*?)\s*(?:#+\s*)?$/gm, '$1')
    .replace(/!\[([^\]]*)\]\(([^)]*)\)/g, '')
    .trim();

export const ProjectCard: React.FC<Props> = ({ className, project, isSpace = false, onFilterOnTopics }) => {
  const { t, i18n } = useTranslation();
  const { processesEndpoint } = useProcessesContext();
  const { antflowServerConfig } = useStoreContext();

  const { externalServiceConfig } = useStoreContext();

  const { toBrowserProjectPath } = useProjectPath();

  const { data: detail, isPending } = useQuery({
    queryKey: ['detail_project', project, processesEndpoint],
    queryFn: async () => {
      const hrefLink = project.links.find((link) => link.rel === 'project')?.href;
      if (!hrefLink) {
        throw new Error('Project link not found');
      }
      return processesEndpoint.get<Project>(hrefLink);
    },
    refetchOnWindowFocus: false
  });

  const canLaunchProject = useMemo(() => {
    return detail?.shared_permissions?.can_read_env ?? false;
  }, [detail]);

  const target = isSpace
    ? AppRoutes.spaces.detail.url(
        toBrowserProjectPath(project.links.find((link) => link.rel === 'project')?.href || '')
      )
    : AppRoutes.projects.detail.url(
        toBrowserProjectPath(project.links.find((link) => link.rel === 'project')?.href || '')
      );

  const deployActionLink = useMemo(() => {
    if (!isSpace || !externalServiceConfig?.spaces?.url) {
      return undefined;
    }

    return externalServiceConfig?.spaces?.url
      .replace('<project_path>', project.path)
      .replace('<project_ref>', project.default_branch || 'main');
  }, [project, isSpace, externalServiceConfig]);

  const { data: previewImageLink } = useQuery({
    queryKey: ['preview', detail],
    enabled: !!detail,
    queryFn: async () => {
      const previewHref = detail?.links.find((link) => link.rel === 'preview')?.href;
      if (!previewHref) {
        return null;
      }
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      return await endpoint.get_image(previewHref).catch(() => null);
    }
  });

  if (isPending || !detail) {
    return (
      <div className={classNames(className)}>
        <div className="border-1 surface-border overflow-hidden h-full" style={{ borderRadius: 14 }}>
          <Skeleton width="100%" height="86px" borderRadius="0" />
          <div className="p-3">
            <Skeleton width="60%" height="1.1rem" className="mb-2" />
            <Skeleton width="100%" height="0.7rem" className="mb-1" />
            <Skeleton width="90%" height="0.7rem" className="mb-3" />
            <div className="flex gap-2 mb-3">
              <Skeleton width="3.5rem" height="1.2rem" borderRadius="6px" />
              <Skeleton width="3.5rem" height="1.2rem" borderRadius="6px" />
            </div>
          </div>
          <Skeleton width="100%" height="3rem" borderRadius="0" />
        </div>
      </div>
    );
  }

  const rawDescription = detail.description?.trim()?.length
    ? detail.description
    : detail.readme
      ? cleanReadme(detail.readme)
      : '';
  const description = rawDescription.length > 120 ? rawDescription.slice(0, 120).trim() + '…' : rawDescription;

  const updatedAt = detail.updated_at
    ? DateTime.fromISO(detail.updated_at)
        .setLocale(i18n.language || 'en')
        .toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' })
    : '—';

  const visibleContributors = (detail.contributors ?? []).slice(0, 3);
  const remainingContributors = Math.max(0, (detail.contributors ?? []).length - visibleContributors.length);

  const topics = detail.topics.filter((topic) => {
    const criteria = [antflowServerConfig.topic, externalServiceConfig?.spaces?.topic || ''];
    return !criteria.includes(topic);
  });

  return (
    <div className={classNames(className)}>
      <Card to={target} aria-label={detail.name}>
        <Banner $previewImg={previewImageLink}>
          <StarChip $onImage={!!previewImageLink} aria-label={`${detail.star_count ?? 0} stars`}>
            <i className="pi pi-star" aria-hidden="true" />
            {detail.star_count ?? 0}
          </StarChip>
          {detail.quality_score && <QualityBadge size="92px" qualityScore={detail.quality_score} />}
        </Banner>

        <div className="flex flex-column flex-1 px-3 pt-3 pb-3 gap-2">
          <span className="font-bold text-base">{detail.name}</span>

          {description && <p className="m-0 text-sm text-color-secondary">{description}</p>}
        </div>

        <div className="flex flex-wrap gap-1 px-3 pb-3">
          {topics.map((topic) => (
            <Tag
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFilterOnTopics?.(topic);
              }}
              style={{ cursor: 'default', fontSize: '0.7rem' }}
              rounded
              className="hover: hover:bg-blue-50 hover:text-primary-700 animation-duration-100"
              key={topic}
              value={topic}
            />
          ))}
        </div>

        <FooterRow>
          <div className="flex flex-1 justify-content-between">
            <div className="flex flex-column min-w-0">
              <FooterLabel>{t(['project.contributors', 'Contributors'])}</FooterLabel>
              {visibleContributors.length > 0 ? (
                <div className="flex align-items-center">
                  <GroupAvatar small contributors={visibleContributors} />
                  {remainingContributors > 0 && (
                    <span className="ml-2 text-xs font-semibold" style={{ color: 'var(--text-color-secondary)' }}>
                      +{remainingContributors}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-color-secondary">—</span>
              )}
            </div>
            <div className="flex flex-column align-items-end">
              <FooterLabel>{t(['project.updated', 'Updated'])}</FooterLabel>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-color)' }}>
                {updatedAt}
              </span>
            </div>
          </div>
          {deployActionLink && (
            <div className="flex align-items-center justify-content-center border-left-1 surface-border pl-3 ml-1">
              <Button
                icon="pi pi-play"
                rounded
                size="small"
                disabled={!canLaunchProject}
                aria-label={t(['spaces.deploy', 'Launch application'])}
                data-pr-classname="text-sm"
                tooltip={canLaunchProject ? t(['spaces.deploy', 'Launch application']) : t(['spaces.launch_disabled'])}
                tooltipOptions={{ position: 'top', showOnDisabled: true, className: 'text-sm' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(deployActionLink, '_blank', 'noopener,noreferrer');
                }}
              />
            </div>
          )}
        </FooterRow>
      </Card>
    </div>
  );
};
